import { NonAnonymousActions } from '../../../pages/orderViewer/utils/constants';
import DocViewerApiServiceBase from '../../api/docViewerServiceBase';
import { IActionButtonCommonProps } from '../../typings/DocumentViewer/actionButtonCommonProps';
import { SupplementalMaterialsStatus } from '../../typings/DocumentViewer/enums';
import DialogCloseListener from '../../utils/dialogCloseListener';
import { executeIfLoggedInAsync } from '../../utils/logonUtils';
import RfeWebDialog from '../../utils/rfeWebDialog';
import ActionButton from './actionButton';
import { Icons } from 'copyright-clearance-center-common-ui';
import { forwardRef } from 'react';

export interface ISupplementalMaterialsButtonPropsBase {
	supplementalMaterialsStatus?: SupplementalMaterialsStatus;
	supplementalMaterialsLinks?: string[];
	supplementalMaterialsCount?: number;
	orderId?: number;
	collectionId?: number;
	apiService?: DocViewerApiServiceBase;
	setStatus?: (status: SupplementalMaterialsStatus) => void;
	orderViewLogonUrl?: string;
}

const SupplementalMaterialsButton: React.ForwardRefExoticComponent<
	ISupplementalMaterialsButtonPropsBase & IActionButtonCommonProps & React.HTMLProps<HTMLButtonElement>
> = forwardRef((props, ref) => {
	const smrequest = 'smrequest';

	const smRequestCallback = (res: { op: string }) => {
		if (res.op === smrequest) {
			props.setStatus(SupplementalMaterialsStatus.SupplementalMaterialsOrderInProgress);
			alert(Prompts.SMRComplete);
		}
	};

	const processSmOrder = async () => {
		await executeIfLoggedInAsync(
			async () => {
				DialogCloseListener.subscribe(smrequest, smRequestCallback);

				props.setStatus(SupplementalMaterialsStatus.ServerRequestInProgress);
				const result = await props.apiService.doManualSupplementalMaterialsRequestAsync(props.orderId);
				props.setStatus(SupplementalMaterialsStatus.ManualSupplementalMaterialRequestAvailable);

				if (result?.status === 'Success') {
					RfeWebDialog.show(`order/SMRequestDlg.aspx?OrderId=${props.orderId}`);
				} else if (result?.status === 'PaymentInfoNeeded') {
					alert(
						localize('You must have a credit card saved on your account to request supplemental materials.')
					);
				}
			},
			props.orderViewLogonUrl,
			NonAnonymousActions.SupplementalRequest
		);
	};

	const handler = () => {
		switch (props.supplementalMaterialsStatus) {
			case SupplementalMaterialsStatus.AutoSupplementalMaterials: {
				gaLogAutoSM('Open from Library Document Viewer', props.collectionId);
				RfeWebDialog.show('library/SupplementalMaterialsLinksDlg.aspx?orderId=' + props.orderId);
				break;
			}
			case SupplementalMaterialsStatus.ManualSupplementalMaterialRequestAvailable: {
				processSmOrder();
				break;
			}
			case SupplementalMaterialsStatus.ManualSupplementalMaterials: {
				window.showSMFiles(props.supplementalMaterialsLinks.join(window.g_sLevel1Sep));
				break;
			}
			case SupplementalMaterialsStatus.SupplementalMaterialsOrderInProgress: {
				alert(Prompts.SMRExists);
				break;
			}
			case SupplementalMaterialsStatus.SupplementalMaterialsOrderCanceled: {
				if (confirm(Prompts.SMCancelled)) {
					processSmOrder();
				}

				break;
			}
		}
	};

	let label = localize('Supplemental data');

	if (!props.isPrimary && props.supplementalMaterialsCount > 0) {
		label += ` (${props.supplementalMaterialsCount})`;
	}

	return (
		<ActionButton
			label={label}
			handler={handler}
			icon={Icons.TypesIcon}
			isPrimary={props.isPrimary}
			badge={props.supplementalMaterialsCount > 0 ? props.supplementalMaterialsCount.toString() : null}
			disabled={props.supplementalMaterialsStatus === SupplementalMaterialsStatus.ServerRequestInProgress}
			className={props.className}
			testId="supplemental-materials-btn"
			controlledBy={props.controlledBy}
			ref={ref}
		/>
	);
});
SupplementalMaterialsButton.displayName = 'SupplementalMaterialsButton';

export default SupplementalMaterialsButton;
